/**
 * Pure helpers behind the global search dropdown: grouping, flattening for
 * keyboard traversal, selection movement, and result -> route resolution.
 *
 * Everything here is deliberately free of React and of the network so the
 * behaviour that is easy to get wrong (group ordering, wrap-around, which row
 * Enter opens) is unit-testable without rendering anything.
 */

import { useAuthStore } from '@/store/auth';
import { kycStatusLabel } from '@/utils/format';
import type { GlobalSearchResult, SearchGroup, SearchTypeSummary } from './types';

// B4/D2: mirrors App.tsx's `/posps/:id` allowedRoles exactly -- broker_employee
// was dropped by D2, so a search hit on a POSP must not link that viewer
// somewhere the route itself would only bounce them out of. Keep in sync with
// the same set in components/common/EntityLink.tsx.
const POSP_LINK_ROLES = new Set(['broker_admin', 'posp', 'super_posp']);

/**
 * Group rows by module, preserving the order the SERVER returned them in.
 *
 * The API returns one globally-ranked list (best match first, then a
 * transactional-before-master-data weight). Grouping by first appearance keeps
 * that ranking visible: the module holding the best match is the first group,
 * so the top row of the dropdown is still the best row overall. Sorting groups
 * alphabetically, or by a fixed module order, would silently throw the server's
 * ranking away.
 */
export function groupResults(
  results: readonly GlobalSearchResult[] | null | undefined,
  typeSummaries: readonly SearchTypeSummary[] | null | undefined = undefined
): SearchGroup[] {
  if (!results || results.length === 0) return [];

  const labelByKey = new Map<string, string>();
  for (const summary of typeSummaries ?? []) {
    if (summary && summary.key) labelByKey.set(summary.key, summary.label);
  }

  const groups: SearchGroup[] = [];
  const byKey = new Map<string, SearchGroup>();

  for (const result of results) {
    if (!result) continue;
    const key = result.type || 'other';
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        label: result.type_label || labelByKey.get(key) || key,
        results: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.results.push(result);
  }

  return groups;
}

/**
 * The dropdown's visual order as a flat list.
 *
 * Keyboard selection indexes into THIS, not into the raw response: once rows
 * are grouped, the on-screen order differs from the response order whenever a
 * module's rows were interleaved with another's, and arrow keys must follow
 * what the user can see.
 */
export function flattenGroups(groups: readonly SearchGroup[] | null | undefined): GlobalSearchResult[] {
  if (!groups || groups.length === 0) return [];
  const flat: GlobalSearchResult[] = [];
  for (const group of groups) {
    for (const result of group.results) flat.push(result);
  }
  return flat;
}

/**
 * Index of the first row of each group within `flattenGroups(groups)`.
 * Lets the renderer label each row with its flat index without a lookup map.
 */
export function groupOffsets(groups: readonly SearchGroup[] | null | undefined): number[] {
  const offsets: number[] = [];
  let running = 0;
  for (const group of groups ?? []) {
    offsets.push(running);
    running += group.results.length;
  }
  return offsets;
}

/**
 * Move the active row by `delta`, wrapping at both ends.
 *
 * `current < 0` means "nothing selected yet": ArrowDown then lands on the first
 * row and ArrowUp on the last, which is what a user expects from a freshly
 * opened list. Returns -1 when there is nothing to select.
 */
export function moveSelection(count: number, current: number, delta: number): number {
  if (!Number.isFinite(count) || count <= 0) return -1;
  if (delta === 0) {
    return current >= 0 && current < count ? current : -1;
  }
  // Out-of-range (including the -1 "unselected" sentinel) starts just outside
  // the edge the movement comes from, so one step lands on the near end.
  const start = current >= 0 && current < count ? current : delta > 0 ? -1 : 0;
  const next = (start + delta) % count;
  return next < 0 ? next + count : next;
}

/** Keep an index inside `[0, count)`, or -1 when there is nothing to select. */
export function clampSelection(count: number, current: number): number {
  if (!Number.isFinite(count) || count <= 0) return -1;
  if (current < 0) return -1;
  return current >= count ? count - 1 : current;
}

/**
 * Fallback route per module, used when the server omits `url`.
 * Every path here is a real route in App.tsx.
 */
const HREF_BUILDERS: Record<string, (id: string) => string> = {
  policy: (id) => `/policies/${id}`,
  customer: (id) => `/contacts/${id}`,
  claim: (id) => `/claims/${id}`,
  quote: (id) => `/quotes/${id}`,
  lead: (id) => `/leads/${id}`,
  // /posps/:id is a redirect-only resolver (LegacyPospRedirect).
  posp: (id) => `/posps/${id}`,
  insurer: (id) => `/admin/insurers/${id}`,
  broker: (id) => `/brokers/${id}`,
};

/**
 * Where clicking a result goes, or null if it is not navigable.
 *
 * Prefers the server's `url` but only when it is an in-app absolute path. A
 * value like `//evil.example` or `https://...` would be an off-site navigation
 * driven by an API response; `react-router`'s navigate would also mangle it.
 * Anything else falls back to `type` + `id`, which is the documented contract.
 *
 * B4/D2: a `posp` row's href goes through `/posps/:id` (LegacyPospRedirect),
 * which no longer admits broker_employee. `viewerRole` lets a caller pass the
 * current user's role explicitly (used by the tests below); when the
 * argument is omitted entirely (as the one production call site,
 * `GlobalSearch.tsx`, does) this reads the live auth store itself, so the
 * gate is real without that file needing a change.
 *
 * That route ALSO requires `channels: read`, which is an ordinary revocable
 * cell even for a posp (it is not in POSP_LOCKED). This module is deliberately
 * React-free, so it cannot call `useCan` itself: `canReadChannels` is how a
 * rendering caller passes the answer in. `false` refuses the link; `undefined`
 * means "not checked" and leaves only the role gate, which is what
 * `GlobalSearch.tsx` gets until that call site passes the cell (see the
 * handover note) -- a POSP whose channels READ was individually revoked can
 * still be handed a search link there.
 */
export function resultHref(
  result: GlobalSearchResult | null | undefined,
  viewerRole?: string | null,
  canReadChannels?: boolean
): string | null {
  if (!result) return null;
  const url = (result.url ?? '').trim();
  let href: string | null = null;
  if (url.startsWith('/') && !url.startsWith('//')) {
    href = url;
  } else {
    const builder = HREF_BUILDERS[result.type];
    if (builder && result.id) href = builder(String(result.id));
  }
  if (!href) return null;
  if (result.type === 'posp') {
    const role = viewerRole !== undefined ? viewerRole : useAuthStore.getState().user?.role ?? null;
    if (!role || !POSP_LINK_ROLES.has(role)) return null;
    if (canReadChannels === false) return null;
  }
  return href;
}

/** Stable React key: ids are only unique within a module. */
export function resultKey(result: GlobalSearchResult, index: number): string {
  return `${result.type}:${result.id ?? index}`;
}

/** The status text on a result row: the KYC wording where it is the KYC state, else underscores as spaces. */
export function resultStatusLabel(status: unknown): string {
  const text = String(status ?? '');
  return text === 'reverify_required' ? kycStatusLabel(text) : text.replace(/_/g, ' ');
}
